import React, { useCallback } from 'react'

import type { RootStateOrAny } from 'react-redux'
import { useDispatch, useSelector } from 'react-redux'

import { trackUTMAndPromoCode } from 'actions/tracking'
import { dismissRecipesInBasketProgress } from 'actions/trackingKeys'
import { useIsBasketComprehensionEnabled } from 'routes/Menu/hooks/useIsBasketComprehensionEnabled'
import { getMenuRecipeUuidForDetails } from 'routes/Menu/selectors/menuRecipeDetails'

import { useBasket } from '../../domains/basket'
import { RecipesInBasketProgressPresentation } from './RecipesInBasketProgress.presentation'
import { RecipesInBasketProgressTreatmentPresentation } from './Treatment/RecipesInBasketProgressTreatment.presentation'

type CloseTrackingFn = (maxRecipes: number, percentage: number) => void

function useTracking() {
  const { recipeCount } = useBasket()
  const dispatch = useDispatch()
  const dispatchTrackUTMAndPromoCode = useCallback<CloseTrackingFn>(
    (maxRecipes, percentage) => {
      dispatch(
        trackUTMAndPromoCode(dismissRecipesInBasketProgress, {
          num_recipes: recipeCount,
          max_recipes: maxRecipes,
          percentage_complete: percentage,
        }),
      )
    },
    [recipeCount, dispatch],
  )

  return { dispatchTrackUTMAndPromoCode, recipeCount }
}

const RecipesInBasketProgress = () => {
  const { dispatchTrackUTMAndPromoCode, recipeCount } = useTracking()
  const isBasketComprehensionEnabled = useIsBasketComprehensionEnabled()
  const isBoxSummaryOpen = useSelector<RootStateOrAny, boolean>((state) =>
    state.boxSummaryShow.get('show'),
  )
  // Same selector DetailOverlay uses to decide whether to show, so the card
  // hides exactly when a recipe detail opens (it's fixed and would overlap it).
  const isRecipeDetailOpen = Boolean(useSelector(getMenuRecipeUuidForDetails))

  if (isBasketComprehensionEnabled) {
    if (isBoxSummaryOpen || isRecipeDetailOpen) {
      return null
    }

    return (
      <RecipesInBasketProgressTreatmentPresentation
        selectedRecipesCount={recipeCount}
        onClose={dispatchTrackUTMAndPromoCode}
      />
    )
  }

  return (
    <RecipesInBasketProgressPresentation
      selectedRecipesCount={recipeCount}
      onClose={dispatchTrackUTMAndPromoCode}
    />
  )
}

export { RecipesInBasketProgress }
